import type * as CSS from 'csstype'
import type React from 'react'
import type { ComponentPropsWithoutRef, PropsWithChildren } from 'react'
import type { TokenFunction } from '../../styling/get-token'
import type { PartialPrefix } from '../../typescript/partial-prefix'
import { css, styled } from 'styled-components'

interface GridProps extends ComponentPropsWithoutRef<'div'> {
  gap?: string | TokenFunction
  /** The number of columns in the grid. */
  cols?: number
  /** CSS property: 'grid-auto-columns' */
  autoColumns?: CSS.Property.GridAutoColumns
  /** CSS property: 'align-items' */
  alignItems?: CSS.Property.AlignItems
  /** CSS property: 'justify-items' */
  justifyItems?: CSS.Property.JustifyItems
  as?: keyof React.JSX.IntrinsicElements
}
type GridStyledProps = PartialPrefix<GridProps, 'gap' | 'cols' | 'autoColumns' | 'alignItems' | 'justifyItems'>

const GridStyled = styled.div<GridStyledProps>`
  display: grid;
  gap: ${props => props.$gap};
  align-items: ${props => props.$alignItems};
  justify-items: ${props => props.$justifyItems};

  // create equal sized columns
  ${props =>
    props.$cols
    && !props.$autoColumns
    && css`
      grid-template-columns: repeat(${props.$cols}, 1fr);
    `}

  // create auto sized columns
  grid-auto-columns: ${props => props.$autoColumns};
  ${props =>
    props.$autoColumns
    && props.$cols
    && css`
      grid-template-areas: '${Array.from({ length: props.$cols }, () => 'section').join(' ')}'; // create "section section" (based on the amount of columns)
    `}
`

/** Utility for creating a CSS grid */
export function Grid({
  as,
  children,
  gap,
  cols,
  alignItems,
  autoColumns,
  justifyItems,
  ...attributes
}: PropsWithChildren<GridProps>): React.JSX.Element {
  return (
    <GridStyled
      as={as}
      $gap={gap}
      $cols={cols}
      $alignItems={alignItems}
      $autoColumns={autoColumns}
      $justifyItems={justifyItems}
      {...attributes}
    >
      {children}
    </GridStyled>
  )
}

Grid.displayName = 'Grid'
